import React, { useState, useMemo } from "react";
import styles from "../styles/jobApplicationStats.module.css";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from "chart.js";

import OverviewTab from "./OverviewTab";
import HistoryTab from "./HistoryTab";
import MetricsTab from "./MetricsTab";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const THEME_COLORS = {
  primaryDark: "#6c34ec",
  primaryLight: "#a78bfa",
  primaryBgLight: "#f3e8ff",
  textMedium: "#374151",
  pageBg: "#f9fafb",
  success: "#10b981",
  error: "#ef4444",
};

const JobApplicationStats = ({
  jobData,
  isLoading,
  startAutomation,
  isLaunchDisabled,
  launchState,
}) => {
  const [activeTab, setActiveTab] = useState("overview");

  const {
    jobHistory = [],
    availableJobs = 0,
    isPremium = false,
    dailyCount = 0,
    monthlyCount = 0,
  } = jobData || {};

  const getTimestampAsDate = (job) => {
    if (job?.appliedAt?._seconds)
      return new Date(job.appliedAt._seconds * 1000);
    if (job?.timestamp) return new Date(job.timestamp);
    return null;
  };

  const getJobDisplayTitle = (job) => {
    if (job?.jobTitle?.trim()) return job.jobTitle;
    if (job?.jobUrl) {
      try {
        const url = new URL(job.jobUrl);
        return (
          url.pathname.split("/").filter(Boolean).pop()?.replace(/-/g, " ") ||
          "Job Link"
        );
      } catch (error) {
        return job.jobUrl;
      }
    }
    return "Untitled Application";
  };

  const getLast7DaysData = () => {
    const data = Array(7).fill(0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    jobHistory.forEach((job) => {
      const jobDate = getTimestampAsDate(job);
      if (!jobDate) return;
      jobDate.setHours(0, 0, 0, 0);
      const diffTime = today.getTime() - jobDate.getTime();
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays >= 0 && diffDays < 7) {
        data[6 - diffDays]++;
      }
    });
    return data;
  };

  const getApplicationTimeDistribution = () => {
    let morning = 0,
      afternoon = 0,
      evening = 0,
      night = 0;
    jobHistory.forEach((job) => {
      const jobDate = getTimestampAsDate(job);
      if (!jobDate) return;
      const hour = jobDate.getHours();
      if (hour >= 5 && hour < 12) morning++;
      else if (hour >= 12 && hour < 17) afternoon++;
      else if (hour >= 17 && hour < 24) evening++;
      else night++;
    });
    return { morning, afternoon, evening, night };
  };

  const getApplicationRateMetrics = () => ({
    dailyPercentage: Math.min((dailyCount / 40) * 100, 100),
    monthlyPercentage: Math.min((monthlyCount / 1000) * 100, 100),
  });

  const formatDate = (job) => {
    const date = getTimestampAsDate(job);
    if (!date) return "N/A";
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  };

  const last7DaysData = getLast7DaysData();
  const timeDistribution = getApplicationTimeDistribution();
  const totalApplications = jobHistory.length;
  const metrics = getApplicationRateMetrics();
  const mostActiveTime = useMemo(() => {
    const entries = Object.entries(timeDistribution);
    if (entries.length === 0 || entries.every((e) => e[1] === 0)) return "N/A";
    const [mostActive] = entries.sort((a, b) => b[1] - a[1]);
    return mostActive[0].charAt(0).toUpperCase() + mostActive[0].slice(1);
  }, [timeDistribution]);

  const barChartData = {
    labels: ["6d", "5d", "4d", "3d", "2d", "1d", "Today"],
    datasets: [
      {
        data: last7DaysData,
        backgroundColor: THEME_COLORS.primaryDark,
        borderRadius: 4,
        barPercentage: 0.5,
      },
    ],
  };
  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false } },
    scales: { y: { display: false }, x: { grid: { display: false } } },
  };

  const doughnutChartData = {
    labels: ["Afternoon", "Evening", "Morning", "Night"],
    datasets: [
      {
        data: [
          timeDistribution.afternoon,
          timeDistribution.evening,
          timeDistribution.morning,
          timeDistribution.night,
        ],
        backgroundColor: [
          THEME_COLORS.primaryDark,
          THEME_COLORS.primaryBgLight,
          THEME_COLORS.primaryLight,
          THEME_COLORS.textMedium,
        ],
        borderColor: THEME_COLORS.pageBg,
        borderWidth: 6,
        borderRadius: 8,
      },
    ],
  };
  const doughnutChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    cutout: "75%",
    plugins: {
      legend: {
        display: true,
        position: "bottom",
        labels: { padding: 20, usePointStyle: true, pointStyle: "circle" },
      },
      tooltip: { enabled: true, padding: 12, backgroundColor: "#232323" },
    },
  };

  const overviewStats = {
    dailyCount,
    monthlyCount,
    totalApplications,
    availableJobs,
  };
  const overviewCharts = {
    barChartData,
    barChartOptions,
    doughnutChartData,
    doughnutChartOptions,
  };
  const metricsStats = {
    isPremium,
    dailyCount,
    monthlyCount,
    totalApplications,
    availableJobs,
    mostActiveTime,
  };

  return (
    <div className={styles.statsWrapper}>
      {isLoading ? (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p>Loading Statistics...</p>
        </div>
      ) : (
        <>
          <div className={styles.tabsContainer}>
            <button
              className={`${styles.tabButton} ${
                activeTab === "overview" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("overview")}
            >
              Overview
            </button>
            <button
              className={`${styles.tabButton} ${
                activeTab === "history" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("history")}
            >
              Application History
            </button>
            <button
              className={`${styles.tabButton} ${
                activeTab === "metrics" ? styles.activeTab : ""
              }`}
              onClick={() => setActiveTab("metrics")}
            >
              Metrics
            </button>
          </div>

          <div className={styles.tabContentContainer}>
            {activeTab === "overview" && (
              <OverviewTab stats={overviewStats} charts={overviewCharts} />
            )}
            {activeTab === "history" && (
              <HistoryTab
                allApplications={jobHistory}
                totalApplications={totalApplications}
                getJobDisplayTitle={getJobDisplayTitle}
                formatDate={formatDate}
              />
            )}
            {activeTab === "metrics" && (
              <MetricsTab stats={metricsStats} metrics={metrics} />
            )}
          </div>
        </>
      )}

      <button
        className={styles.startAutomationBtn}
        onClick={startAutomation}
        disabled={isLaunchDisabled}
        title={launchState.text}
      >
        <i className="fas fa-play-circle"></i>
        <span className={styles.buttonText}>{launchState.text}</span>
      </button>
    </div>
  );
};

export default JobApplicationStats;
